import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Stack } from "@mui/joy";
import dayjs, { Dayjs } from "dayjs";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import CustomModal from "components/common/CustomModal";
import ContentTemplateV2 from "components/task/content/ContentTemplateV2";
import TaskHeader from "components/task/header/TaskHeader";
import FloatingArea from "components/common/FloatingArea";
import useGoBack from "hooks/useGoBack";
import useIsInappWebview from "hooks/useIsInappWebview";
import useMixpanelWeb from "hooks/useMixpanelWeb";
import useSubscriptionStatus from "hooks/useSubscriptionStatus";
import { SubscriptionPauseType } from "api/subscriptionPauseApi";
import {
  useCancelSubscriptionPause,
  useCreateSubscriptionPause,
  useSubscriptionPause,
  useUpdateSubscriptionPause,
} from "hooks/useSubscriptionPause";
import {
  clampEndDate,
  durationDaysInclusive,
  formatPausePeriodText,
  getExhaustMessage,
  getLastPausableDay,
  getMaxEndDate,
  getRemainingCount,
  getRemainingDays,
  getTomorrowKST,
} from "utils/subscriptionPauseUtils";
import PauseDateRow from "components/mypage/PauseDateRow";

// 예약/변경 폼 — pause 가 로드된 뒤에만 마운트되어 초기값을 한 번 확정한다.
function PauseForm({ pause }: { pause: SubscriptionPauseType }) {
  const goBack = useGoBack();
  const isInappWebview = useIsInappWebview();
  const { expirationAt } = useSubscriptionStatus();
  const createPause = useCreateSubscriptionPause();
  const updatePause = useUpdateSubscriptionPause();
  const cancelPause = useCancelSubscriptionPause();

  const reservation = pause.reservation;
  const isEdit = reservation?.status === "reserved";

  // 예약 불가 → 읽기전용 폼(날짜·CTA 비활성, 안내 메시지). 환불 설문에서 아래 상태로
  // 진입했을 때 사용한다(결제내역에선 이 상태로 화면에 도달하지 않음):
  //  - active: 지금 일시정지 중 → 진행 기간 안내
  //  - 예약 모드가 아닌데 자격없음(!isAvailable)·잔여 소진 → 소진/불가 안내
  const isActive = reservation?.status === "active";
  const exhausted =
    getRemainingDays(pause) <= 0 || getRemainingCount(pause) <= 0;
  const readOnly = isActive || (!isEdit && (!pause.isAvailable || exhausted));
  const readOnlyMessage =
    isActive && reservation
      ? `${formatPausePeriodText(
          dayjs(reservation.startDate),
          dayjs(reservation.endDate)
        )} 프로그램 이용을 잠시 중단한 상태입니다.`
      : (getExhaustMessage(pause) ?? "현재 일시정지를 사용할 수 없습니다.");

  // 변경 시에는 기존 예약분이 used 에 포함돼 있으므로 되돌려 가용일을 계산한다.
  const reservationDuration = reservation
    ? durationDaysInclusive(
        dayjs(reservation.startDate),
        dayjs(reservation.endDate)
      )
    : 0;
  const availableDays = isEdit
    ? getRemainingDays(pause) + reservationDuration
    : getRemainingDays(pause);

  const minStart = getTomorrowKST();
  // 일시정지 구간은 현재 구독 만료일보다 이전이어야 함 → 선택 가능한 마지막 날 = 만료일−1.
  const lastPausableDay = getLastPausableDay(expirationAt);

  // active·reserved 모두 예약 구간을 그대로 표기(active 는 읽기전용으로 진행 기간 노출).
  const [start, setStart] = useState<Dayjs>(() =>
    reservation ? dayjs(reservation.startDate) : minStart
  );
  const [end, setEnd] = useState<Dayjs>(() =>
    reservation
      ? dayjs(reservation.endDate)
      : getMaxEndDate(minStart, availableDays, lastPausableDay)
  );

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const handleStartChange = (next: Dayjs) => {
    setStart(next);
    setEnd((prevEnd) =>
      clampEndDate(next, prevEnd, availableDays, lastPausableDay)
    );
  };
  const handleEndChange = (next: Dayjs) => {
    setEnd(clampEndDate(start, next, availableDays, lastPausableDay));
  };

  const changed =
    isEdit && reservation
      ? !start.isSame(dayjs(reservation.startDate), "day") ||
        !end.isSame(dayjs(reservation.endDate), "day")
      : true;

  const requestBody = {
    startDate: start.format("YYYY-MM-DD"),
    endDate: end.format("YYYY-MM-DD"),
  };

  // Mixpanel (메인 앱 인스턴스) — 진입 1종 + 완료 3종. 매 렌더 최신 params 로 갱신됨.
  const trackEnter = useMixpanelWeb({
    eventName: "일시정지 예약 진입",
    params: {
      mode: isEdit ? "edit" : "create",
      remaining_days: String(getRemainingDays(pause)),
      remaining_count: String(getRemainingCount(pause)),
      available_days: String(availableDays),
    },
  });
  const trackCreate = useMixpanelWeb({
    eventName: "일시정지 예약 완료",
    params: {
      start_date: requestBody.startDate,
      end_date: requestBody.endDate,
      days: String(durationDaysInclusive(start, end)),
    },
  });
  const trackUpdate = useMixpanelWeb({
    eventName: "일시정지 예약 변경 완료",
    params: {
      start_date: requestBody.startDate,
      end_date: requestBody.endDate,
      days: String(durationDaysInclusive(start, end)),
    },
  });
  const trackCancel = useMixpanelWeb({
    eventName: "일시정지 예약 취소 완료",
    params: {
      start_date: reservation?.startDate ?? "",
      end_date: reservation?.endDate ?? "",
      days: reservation
        ? String(
            durationDaysInclusive(
              dayjs(reservation.startDate),
              dayjs(reservation.endDate)
            )
          )
        : "",
    },
  });

  // 마운트 1회 발사 (StrictMode 중복 방지) — PlanUpgradeScreen 패턴.
  const enteredRef = useRef(false);
  useEffect(() => {
    if (enteredRef.current) return;
    enteredRef.current = true;
    trackEnter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = () => {
    const mutation = isEdit ? updatePause : createPause;
    mutation.mutate(requestBody, {
      onSuccess: () => {
        (isEdit ? trackUpdate : trackCreate)();
        goBack();
      },
    });
  };

  const handleCancelReservation = () => {
    cancelPause.mutate(undefined, {
      onSuccess: () => {
        trackCancel();
        goBack();
      },
    });
  };

  const periodText = formatPausePeriodText(start, end);
  const isMutating =
    createPause.isLoading || updatePause.isLoading || cancelPause.isLoading;

  return (
    <>
      {/* 나탭 뎁스 페이지(알림 추가/핀 설정)와 동일: 고정 TaskHeader (배경 Level1 상속) */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 100 }}>
        <TaskHeader
          taskKey={"subscription-pause"}
          title={isEdit ? "일시정지 예약 변경" : "일시정지 예약"}
        />
      </Box>

      <ContentTemplateV2>
        <Stack
          sx={{
            borderRadius: "16px",
            backgroundColor: theme.vars.palette.Surface.Forward,
            p: 1,
          }}
        >
          <PauseDateRow
            label={"시작일"}
            value={start}
            onChange={handleStartChange}
            minDate={minStart}
            maxDate={lastPausableDay}
            disabled={readOnly}
          />
          <PauseDateRow
            label={"종료일"}
            value={end}
            onChange={handleEndChange}
            minDate={start}
            maxDate={getMaxEndDate(start, availableDays, lastPausableDay)}
            disabled={readOnly}
          />
        </Stack>

        <Stack spacing={1} px={1} mt={"12px"}>
          {readOnly ? (
            <CustomTypography
              variant={"Caption"}
              sx={{ color: theme.vars.palette.Content.Weak, fontWeight: 500 }}
            >
              {readOnlyMessage}
            </CustomTypography>
          ) : (
            <>
              <CustomTypography
                variant={"Caption"}
                sx={{ color: theme.vars.palette.Content.Weak, fontWeight: 500 }}
              >
                {"지정한 시작일부터 종료일까지 코칭이 중단됩니다."}
              </CustomTypography>
              <CustomTypography
                variant={"Caption"}
                sx={{ color: theme.vars.palette.Content.Weak, fontWeight: 500 }}
              >
                {"일시정지는 되돌릴 수 없으니 신중히 결정해주세요."}
              </CustomTypography>
            </>
          )}
        </Stack>

        {/* 하단 FloatingArea 에 가리지 않도록 여백 */}
        <Box sx={{ height: "120px" }} />
      </ContentTemplateV2>

      {/* 나탭 뎁스 페이지와 동일: FloatingArea (배경 Level1, PC 사이드바 offset 반영) */}
      <FloatingArea fullWidth={!!isInappWebview} noDivider>
        {isEdit ? (
          <Stack direction={"row"} spacing={"8px"} sx={{ width: "100%" }}>
            <Button
              variant={"Outlined"}
              size={"lg"}
              sx={{ flex: 1 }}
              onClick={() => setCancelOpen(true)}
            >
              {"예약 취소하기"}
            </Button>
            <Button
              variant={"Action"}
              size={"lg"}
              sx={{ flex: 1 }}
              disabled={!changed}
              onClick={() => setConfirmOpen(true)}
            >
              {"변경하기"}
            </Button>
          </Stack>
        ) : (
          <Button
            variant={"Action"}
            size={"lg"}
            fullWidth
            disabled={readOnly}
            onClick={() => setConfirmOpen(true)}
          >
            {"예약하기"}
          </Button>
        )}
      </FloatingArea>

      {/* 예약/변경 confirm */}
      <CustomModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={
          isEdit ? "일시정지 예약을 변경할까요?" : "정말로 일시정지할까요?"
        }
        subtitle={`${periodText} 프로그램 이용을 잠시 중단합니다.`}
        actions={[
          {
            label: "확인",
            onClick: handleConfirm,
            variant: "Solid",
            buttonProps: { loading: isMutating },
          },
        ]}
      />

      {/* 예약 취소 confirm */}
      <CustomModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={"일시정지 예약을 취소할까요?"}
        subtitle={"예약이 취소되며 일시정지 가능 기간과 횟수가 복구됩니다."}
        actions={[
          {
            label: "확인",
            onClick: handleCancelReservation,
            variant: "Solid",
            buttonProps: { loading: cancelPause.isLoading },
          },
        ]}
      />
    </>
  );
}

export default function SubscriptionPauseScreen() {
  const { data: pause, isLoading } = useSubscriptionPause();

  // 예약 불가 상태(일시정지 중·자격없음·잔여소진)는 PauseForm 이 읽기전용으로 렌더한다.
  // 결제내역(PauseStatusSection)에서는 그 상태들이 팝업/숨김으로 차단돼 이 화면에
  // 도달하지 않고, 환불 설문에서 진입한 경우에만 읽기전용 폼+안내 메시지를 보여준다.
  if (isLoading || !pause) return null;

  return <PauseForm pause={pause} />;
}
